/**
 * Все чат-сессии в системе с фильтрами. Mobile: cards.
 */

import { Alert, Card, Center, Group, Loader, Stack, Table, Text, TextInput, Title } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchAdminChats, type ChatSessionRow } from '../../lib/api-admin';
import { DESKTOP_QUERY } from '../../lib/breakpoints';
import { fmtRelative } from './format';

export function AdminChatsPage(): JSX.Element {
    const [user, setUser]   = useState('');
    const [owner, setOwner] = useState('');
    const [repo, setRepo]   = useState('');
    const isDesktop = useMediaQuery(DESKTOP_QUERY);

    const q = useQuery({
        queryKey: ['admin-chats', user, owner, repo],
        queryFn:  () => fetchAdminChats({ user: user || undefined, owner: owner || undefined, repo: repo || undefined, limit: 200 }),
        retry: false,
    });

    return (
        <Stack gap="md">
            <Title order={2}>Чаты</Title>
            <Group gap="sm" wrap="wrap">
                <TextInput placeholder="user (email)" value={user}  onChange={e => setUser(e.currentTarget.value)}  flex={1} miw={180} />
                <TextInput placeholder="owner"        value={owner} onChange={e => setOwner(e.currentTarget.value)} w={140} />
                <TextInput placeholder="repo"         value={repo}  onChange={e => setRepo(e.currentTarget.value)}  w={140} />
            </Group>
            {q.isLoading && <Center my="lg"><Loader /></Center>}
            {q.error && <Alert color="red">{(q.error as Error).message}</Alert>}
            {q.data && (
                <>
                    <Text fz="sm" c="dimmed">Найдено: {q.data.total}</Text>
                    {isDesktop ? <DesktopTable rows={q.data.sessions} /> : <MobileList rows={q.data.sessions} />}
                </>
            )}
        </Stack>
    );
}

function DesktopTable({ rows }: { rows: ChatSessionRow[] }): JSX.Element {
    return (
        <Card withBorder padding={0}>
            <Table fz="sm" striped highlightOnHover verticalSpacing="sm">
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Заголовок</Table.Th>
                        <Table.Th>User</Table.Th>
                        <Table.Th>Репо</Table.Th>
                        <Table.Th>Сообщений</Table.Th>
                        <Table.Th>Обновлено</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {rows.map(r => (
                        <Table.Tr key={r.id}>
                            <Table.Td><Link to={`/admin/chats/${encodeURIComponent(r.id)}`}>{r.title || '(без заголовка)'}</Link></Table.Td>
                            <Table.Td><Text fz="xs">{r.user}</Text></Table.Td>
                            <Table.Td>{r.owner}/{r.repo}</Table.Td>
                            <Table.Td>{r.messageCount}</Table.Td>
                            <Table.Td><Text fz="xs">{fmtRelative(r.updatedAt)}</Text></Table.Td>
                        </Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>
        </Card>
    );
}

function MobileList({ rows }: { rows: ChatSessionRow[] }): JSX.Element {
    return (
        <Stack gap="xs">
            {rows.map(r => (
                <Card withBorder padding="sm" key={r.id}>
                    <Text fw={500} truncate>
                        <Link to={`/admin/chats/${encodeURIComponent(r.id)}`}>{r.title || '(без заголовка)'}</Link>
                    </Text>
                    <Group gap="xs" fz="xs" c="dimmed" wrap="wrap">
                        <Text fz="xs">{r.user}</Text>
                        <Text fz="xs">·</Text>
                        <Text fz="xs">{r.owner}/{r.repo}</Text>
                        <Text fz="xs">·</Text>
                        <Text fz="xs">{r.messageCount} сообщ.</Text>
                        <Text fz="xs">·</Text>
                        <Text fz="xs">{fmtRelative(r.updatedAt)}</Text>
                    </Group>
                </Card>
            ))}
        </Stack>
    );
}
